import React from 'react';
import {
  Box,
  Stack,
  Switch,
  Typography,
  IconButton,
  Chip,
  Button,
  alpha,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { DayOfWeek, DAY_LABELS } from './types';
import { TimeInput } from './TimeInput';
import { useTimeFormat } from '../../context/TimeFormatContext';

interface TimeSlot {
  open: string;
  close: string;
}

interface DayHoursRowProps {
  day: DayOfWeek;
  enabled: boolean;
  timeSlots: TimeSlot[];
  onToggleDay: (enabled: boolean) => void;
  onUpdateTimeSlot: (index: number, field: 'open' | 'close', value: string) => void;
  onAddTimeSlot: () => void;
  onRemoveTimeSlot: (index: number) => void;
  error?: string;
  isToday?: boolean;
  use24Hour?: boolean;
}

export const DayHoursRow: React.FC<DayHoursRowProps> = ({
  day,
  enabled,
  timeSlots,
  onToggleDay,
  onUpdateTimeSlot,
  onAddTimeSlot,
  onRemoveTimeSlot,
  error,
  isToday = false,
  use24Hour,
}) => {
  const theme = useTheme();
  const { use24HourFormat } = useTimeFormat();
  const actualUse24Hour = use24Hour !== undefined ? use24Hour : use24HourFormat;

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: error ? 'error.main' : 'divider',
        bgcolor: isToday ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[1],
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={3}>
        {/* Day Label Column */}
        <Stack sx={{ minWidth: 140 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {DAY_LABELS[day]}
            </Typography>
            {isToday && <Chip label="Today" size="small" color="primary" sx={{ height: 20 }} />}
          </Stack>
        </Stack>

        {/* Time Slots Column */}
        <Box sx={{ flex: 1 }}>
          {enabled ? (
            <Stack spacing={1.5}>
              {timeSlots.length > 0 ? (
                timeSlots.map((slot, index) => (
                  <Stack key={index} direction="row" alignItems="center" spacing={1.5}>
                    {index > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 35, textAlign: 'right' }}
                      >
                        and
                      </Typography>
                    )}
                    <TimeInput
                      value={slot.open}
                      onChange={(value) => onUpdateTimeSlot(index, 'open', value)}
                      use24Hour={actualUse24Hour}
                    />
                    <Typography variant="body2" color="text.secondary">
                      to
                    </Typography>
                    <TimeInput
                      value={slot.close}
                      onChange={(value) => onUpdateTimeSlot(index, 'close', value)}
                      use24Hour={actualUse24Hour}
                    />
                    {timeSlots.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveTimeSlot(index)}
                        sx={{ color: 'error.main' }}
                        title="Remove time slot"
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    )}
                    {index === timeSlots.length - 1 && timeSlots.length < 3 && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onAddTimeSlot}
                        variant="text"
                        sx={{
                          ml: 1,
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        Add hours
                      </Button>
                    )}
                  </Stack>
                ))
              ) : (
                // Default time slots when enabled but no hours set
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <TimeInput
                    value="10:30"
                    onChange={(value) => {
                      onAddTimeSlot();
                      setTimeout(() => onUpdateTimeSlot(0, 'open', value), 0);
                    }}
                    use24Hour={actualUse24Hour}
                  />
                  <Typography variant="body2" color="text.secondary">
                    to
                  </Typography>
                  <TimeInput
                    value="22:45"
                    onChange={(value) => {
                      if (timeSlots.length === 0) onAddTimeSlot();
                      setTimeout(() => onUpdateTimeSlot(0, 'close', value), 0);
                    }}
                    use24Hour={actualUse24Hour}
                  />
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddTimeSlot}
                    variant="text"
                    sx={{
                      ml: 1,
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    Add hours
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Closed
            </Typography>
          )}
        </Box>

        {/* Actions Column */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body2"
              color={enabled ? 'success.main' : 'text.secondary'}
              fontWeight="medium"
            >
              {enabled ? 'Open' : 'Closed'}
            </Typography>
            <Switch
              checked={enabled}
              onChange={(e) => onToggleDay(e.target.checked)}
              size="small"
              color="primary"
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Error Message */}
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
